import { gql } from "@apollo/client";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { BookingViewDataQuery, BookingViewDataQueryVariables } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";
import BookingDataView from "./BookingDataView";

type BookingViewModel = NonNullable<BookingViewDataQuery['booking']>;

type BookingViewPropsType = {
  bookingId: string | number,
  name: string
}

const query = gql`
query BookingViewData($id: String, $lang: String!) {
  booking(ref: $id) {
    id
    person {
      name
      id
      contactInfo {
        items {
          name
          link
          value
          id
        }
      }
      countryCode
      country {
        alpha3Code
        name
        flag
        translate(lang: $lang)
      }
    }
    inDate
    outDate
    nights
    isCancelled
    isNoShowCancellation
    cancelledDate
    hasConflict
    children
    guestReservationLinkExtension
    guestCount
    inSeasonId
    bookingDate
    note
    total
    paidAmount
    roomType {
      color
      name
      id
    }
    channel {
      channel {
        name
      }
      total
      externalReference
      isCancelled
    }
    season {
      name
    }
    details {
      items {
        room {
          name
          roomType {
            color
            name
            id
          }
          id
        }
        price
        date
        id
        hasConflict
      }
      priceSum
    }
    additionalCharge {
      items {
        amount
        date
        name
        id
      }
      amountSum
    }
  }
}
  `;

const BookingView = (props: BookingViewPropsType) => {
  const context = useAppContext();
  const [model, setModel] = useState<BookingViewModel>();
  const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');

  // #region load data
  useEffect(() => {
    loadData().then();
  }, [props.bookingId]);

  const loadData = async () => {
    setComponentState('loading');
    try {
      const response = await graph<BookingViewDataQuery, BookingViewDataQueryVariables>({
        query: query,
        variables: {
          id: props.bookingId.toString(),
          lang: context.language.code
        }
      });

      if (response.errors) {
        throw response.errors;
      }

      const data = response.data;
      if (!data?.booking) {
        setComponentState('nodata');
      } else {
        setModel(response.data!.booking!);
        setComponentState('');
      }
    } catch (error: any) {
      context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
      setComponentState('error');
      throw error;
    }
  };
  // #endregion load data

  return (
    <ComponentState state={componentState}>
      <BookingDataView name={props.name} model={model!} />
    </ComponentState>
  );
};

export default BookingView;
